import * as React from 'react'
import { Row, Updater } from '@tanstack/react-table'
import {
  SelectAdvanceComponents,
  SelectAdvanceProps,
  Typography,
  SelectAdvancePopover,
  useDebounceValue,
} from '@applift/factor'
import { RowSelectionState } from '@applift/datagrid'
import { getPrebidChildSegmentDetailByParentId } from '../../api/PreBidAudience'
import {
  usePrebidSearchSegmentData,
  usePrebidProviderData,
} from '../../hooks/usePreBidAudience'
import {
  PrebidNormalizedSegmentData,
  PrebidProviderData,
} from '../../models/PreBidAudience'
import { ListItemRenderer, renderOption } from './renderers'
import { transformInitDataToRowSelection } from './helpers'

type SelectPrebidSegmentChangeReason = 'userAction' | 'apiAction'

const defaultWarningText =
  'Only one creative type (Display or Video) is supported per campaign; So, avoid grouping respective pre-bid segments in one audience.'

export interface SelectPrebidSegmentProps
  extends Omit<
    SelectAdvanceProps<PrebidNormalizedSegmentData>,
    | 'data'
    | 'rowCount'
    | 'hideSelectNav'
    | 'search'
    | 'hideSearch'
    | 'loading'
    | 'renderContentTop'
    | 'overlay'
    | 'renderListItem'
    | 'onFetchSubRows'
    | 'enableSubRowSelection'
    | 'getSubRows'
    | 'renderOption'
    | 'isRowLoading'
    | 'value'
    | 'onChange'
    | 'onInputChange'
  > {
  initialData?: Record<string, PrebidNormalizedSegmentData>
  onChange?: (
    data: Record<string, PrebidNormalizedSegmentData>,
    reason?: SelectPrebidSegmentChangeReason
  ) => void
  warningText?: string
}

export interface SelectPrebidSegmentApiRefType {
  getRowSelection: () => RowSelectionState
  getRowSelectionData: () => Map<string, PrebidNormalizedSegmentData>
  getCurrentData: () => PrebidNormalizedSegmentData[]
  removeSelection: (value: string) => void
  setSelection: (rowId: string) => void
  reinitializeRowSelection: (
    initData: Record<string, PrebidNormalizedSegmentData>
  ) => void
  getSegmentCache: () => Map<string, PrebidNormalizedSegmentData[]>
  clearSegmentCache: () => void
}

export const SelectPrebidSegment = React.forwardRef<
  SelectPrebidSegmentApiRefType | undefined,
  SelectPrebidSegmentProps
>((props, ref) => {
  const {
    initialData,
    onChange,
    warningText = defaultWarningText,
    ...others
  } = props

  const apiRef =
    SelectAdvanceComponents.useSelectAdvanceApiRef<PrebidNormalizedSegmentData>()

  const [data, setData] = React.useState<PrebidNormalizedSegmentData[]>([])
  const dataRef = React.useRef(data)
  dataRef.current = data

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(
    transformInitDataToRowSelection(initialData)
  )
  const rowSelectionRef = React.useRef(rowSelection)
  rowSelectionRef.current = rowSelection

  const rowSelectionData = React.useRef(
    new Map<string, PrebidNormalizedSegmentData>(
      Object.entries(initialData ?? {})
    )
  )

  const [overlay, setOverlay] = React.useState<
    'noResult' | 'noRows' | 'error' | undefined
  >(undefined)

  const [rowsLoader, setRowsLoader] = React.useState<RowSelectionState>({})
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebounceValue(search, 250)

  const segmentsMapCache = React.useRef<
    Map<string, PrebidNormalizedSegmentData[]>
  >(new Map<string, PrebidNormalizedSegmentData[]>())

  // API function to remove selection
  const removeSelectionFn: SelectPrebidSegmentApiRefType['removeSelection'] =
    value => {
      rowSelectionData.current.delete(value)
      const rowSelectionCloned = { ...rowSelection }
      delete rowSelectionCloned[value]
      setRowSelection(rowSelectionCloned)
      if (typeof onChange === 'function') {
        onChange(
          Object.fromEntries(rowSelectionData.current.entries()),
          'apiAction'
        )
      }
    }

  // API function to perform selection
  const setRowSelectionFn: SelectPrebidSegmentApiRefType['setSelection'] =
    rowId => {
      try {
        const row = apiRef.current.tableInstance.getRow(rowId)
        if (row) {
          rowSelectionData.current.set(row.id, row.original)
          setRowSelection({ ...rowSelection, [row.id]: true })
          if (typeof onChange === 'function') {
            onChange(
              Object.fromEntries(rowSelectionData.current.entries()),
              'apiAction'
            )
          }
        }
      } catch (e) {
        console.log(e)
        console.log('cannot add row that is not part of current dataset')
        // ignore error silenty
      }
    }

  // API function to perform reinitialization of selection
  const reinitializeRowSelectionFn: SelectPrebidSegmentApiRefType['reinitializeRowSelection'] =
    initData => {
      const rowSelection: RowSelectionState = {}
      rowSelectionData.current.clear()
      for (const [key, val] of Object.entries(initData)) {
        rowSelection[key] = true
        rowSelectionData.current.set(key, val)
      }
      setRowSelection(rowSelection)
      if (typeof onChange === 'function') {
        onChange(
          Object.fromEntries(rowSelectionData.current.entries()),
          'apiAction'
        )
      }
    }

  React.useImperativeHandle(ref, () => ({
    getRowSelection: () => rowSelectionRef.current,
    getRowSelectionData: () => rowSelectionData.current,
    getCurrentData: () => dataRef.current,
    removeSelection: removeSelectionFn,
    setSelection: setRowSelectionFn,
    reinitializeRowSelection: reinitializeRowSelectionFn,
    getSegmentCache: () => segmentsMapCache.current,
    clearSegmentCache: () => segmentsMapCache.current.clear(),
  }))

  const showWarning = React.useMemo(() => {
    if (Object.keys(rowSelection).length > 0) {
      const rowSelectionObject = rowSelectionData.current.entries()
      const firstValue = rowSelectionObject.next()
      if (firstValue?.value?.[1]) {
        const firstSegment = firstValue.value[1] as PrebidNormalizedSegmentData
        let lastCreativeType = firstSegment.creativeTypeIdSerialized
        for (const [_, segment] of rowSelectionObject) {
          if (
            segment.creativeTypeIdSerialized !== lastCreativeType &&
            segment.isAllCreativeType === false
          ) {
            return true
          } else if (segment.isAllCreativeType === false) {
            lastCreativeType = segment.creativeTypeIdSerialized
          }
        }
      }
    }
    return false
  }, [rowSelection])

  const providerData = usePrebidProviderData()

  const providerDataObject = React.useMemo(() => {
    if (providerData.isSuccess) {
      return providerData.data.prebidSegmentData.reduce(
        (prev, one) => {
          prev[one.providerId] = {
            providerId: one.providerId,
            providerLogoUrl: one.providerLogoUrl,
            providerName: one.providerName,
            description: one.description,
          } as Omit<PrebidProviderData, 'childSegments'>
          return prev
        },
        {} as Record<string, Omit<PrebidProviderData, 'childSegments'>>
      )
    }
    return {} as Record<string, Omit<PrebidProviderData, 'childSegments'>>
  }, [providerData.isSuccess, providerData.data])

  const searchedData = usePrebidSearchSegmentData(debouncedSearch, {
    enabled:
      Boolean(debouncedSearch) === true &&
      Object.keys(providerDataObject).length >= 1,
    meta: providerDataObject,
  })

  React.useEffect(() => {
    if (Boolean(debouncedSearch) === false) {
      // For provider data
      apiRef.current.tableInstance.resetExpanded()
      const result = providerData.data?.prebidSegmentData ?? []
      if (result.length <= 0 && providerData.isLoading === false) {
        setOverlay('noRows')
      } else {
        setOverlay(undefined)
      }
      setData(providerData.data?.prebidSegmentData ?? [])
    } else {
      // For search data
      apiRef.current.tableInstance.setExpanded(true)
      const result = searchedData.data?.prebidSegmentData ?? []
      if (result.length <= 0 && searchedData.isLoading === false) {
        setOverlay('noResult')
      } else {
        setOverlay(undefined)
      }
      setData(searchedData.data?.prebidSegmentData ?? [])
    }
  }, [
    providerData.isLoading,
    searchedData.isLoading,
    providerData.data?.prebidSegmentData,
    searchedData.data?.prebidSegmentData,
    debouncedSearch,
    apiRef,
    setOverlay,
    setData,
  ])

  const onFetchSubRows = async ({
    row,
    insertNestedData,
  }: {
    row: Row<PrebidNormalizedSegmentData>
    insertNestedData: <T extends Record<string, any>>({
      existingRows,
      subRowsToInsert,
      path,
      getRowId,
      subRowKey,
    }: {
      existingRows: T[]
      subRowsToInsert: T[]
      path: string[]
      getRowId: (row: T) => string
      subRowKey: string
    }) => T[]
  }) => {
    setRowsLoader(prev => ({ ...prev, [row.id]: true }))
    try {
      let dataToInsert: PrebidNormalizedSegmentData[] = []
      if (segmentsMapCache.current.has(row.original.preservedValue)) {
        dataToInsert = segmentsMapCache.current.get(
          row.original.preservedValue
        )!
      } else {
        const result = await getPrebidChildSegmentDetailByParentId({
          prebidAudienceSegmentId: Number(row.original.preservedValue),
          providerDetails: {
            providerId: row.original.providerId,
            providerName: row.original.providerName,
            providerLogoUrl: row.original.providerLogoUrl,
            description: '',
            childSegments: [],
          },
        })
        dataToInsert = result.prebidSegmentData
        segmentsMapCache.current.set(
          row.original.preservedValue,
          result.prebidSegmentData
        )
      }

      const parentPath = [...row.getParentRows(), row].map(one => one.id)
      setData(prev => {
        return insertNestedData({
          existingRows: prev ?? [],
          subRowsToInsert: dataToInsert,
          path: parentPath,
          getRowId: row => row.value,
          subRowKey: 'subRows',
        })
      })
    } catch (e) {
      // do nothing
      console.log(e)
    }
    // stop loading
    setRowsLoader(old => {
      const rowsLoader = { ...old }
      delete rowsLoader[row.id]
      return rowsLoader
    })
  }

  // Here from the selectedRowsMap we filter out the rows that are part of current data
  // only include selection that is part of current selection
  const getSelectedRowsForCurrentData = () => {
    const selectedRowsEntries = rowSelectionData.current.entries()
    const allRowsById = apiRef.current.tableInstance.getRowModel().rowsById
    const filteredSelectedRows: Record<string, PrebidNormalizedSegmentData> = {}
    for (const selectedRow of selectedRowsEntries) {
      const [id, row] = selectedRow
      if (allRowsById[id]) {
        filteredSelectedRows[id] = row
      }
    }
    return filteredSelectedRows
  }

  const setRowSelectionWrapper = (updater: Updater<RowSelectionState>) => {
    const selectedRows =
      typeof updater === 'function' ? updater(rowSelection) : updater

    const selectedRowsArray = Object.keys(selectedRows)
    const currentSelectedRows = getSelectedRowsForCurrentData()
    const currentSelectedRowsArray = Object.keys(currentSelectedRows)
    const allRowsById = apiRef.current.tableInstance.getRowModel().rowsById

    const additions = selectedRowsArray.filter(
      one => currentSelectedRows[one] === undefined
    )
    const removals = currentSelectedRowsArray.filter(
      one => selectedRows[one] === undefined
    )

    for (const one of additions) {
      // there are cases where we get invalid selection when currentSelectedRows are zero
      // so we want to guard against non existent selections
      if (allRowsById[one]?.original) {
        rowSelectionData.current.set(one, allRowsById[one]!.original!)
      }
    }
    for (const one of removals) {
      rowSelectionData.current.delete(one)
    }
    setRowSelection(updater)
    if (typeof onChange === 'function') {
      onChange(
        Object.fromEntries(rowSelectionData.current.entries()),
        'userAction'
      )
    }
  }

  const RowsSelectedCount = React.useMemo(
    () => Object.keys(rowSelection).length,
    [rowSelection]
  )

  return (
    // @ts-ignore
    <SelectAdvancePopover
      size="small"
      {...others}
      data={data ?? []}
      apiRef={apiRef}
      rowCount={data.length ?? 5}
      inputValue={search}
      multiple
      hideSelectNav
      hideSearch={false}
      loading={
        Boolean(debouncedSearch) === true
          ? searchedData.isLoading
          : providerData.isLoading
      }
      renderContentTop={
        <Typography sx={{ px: 16, pt: 8, pb: 0 }}>
          {RowsSelectedCount} Segment
          {RowsSelectedCount > 1 ? 's' : ''} Selected
        </Typography>
      }
      overlay={overlay}
      renderListItem={ListItemRenderer}
      onFetchSubRows={onFetchSubRows}
      enableSubRowSelection={false}
      getSubRows={row =>
        (row.subRows?.length ?? 0) > 0 ? row.subRows : undefined
      }
      getRowCanExpand={row => {
        return !row.original?.isLeaf ?? false
      }}
      renderOption={rowObject =>
        renderOption(rowObject.row, showWarning, warningText)
      }
      isRowLoading={rowsLoader}
      value={rowSelection}
      onChange={setRowSelectionWrapper}
      onInputChange={(_, newSearchVal, reason) => {
        if (reason !== 'reset') {
          setSearch(newSearchVal)
        }
      }}
      slotProps={{
        ...others.slotProps,
        PaperProps: {
          style: {
            maxWidth: '576px',
            width: 'calc(100% - 64px)',
          },
          ...others.slotProps?.PaperProps,
        },
      }}
    />
  )
})

SelectPrebidSegment.displayName = 'SelectPrebidSegment'
